// LinesTab.jsx - Main Lines tab component
import React, { useState, useMemo } from 'react';
import { Box, Grid, Tabs, TextInput, Group, Text, Checkbox, NumberInput, Button } from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import RefTab from './RefTab';
import ChargesTab from './ChargesTab';
import CostTab from './CostTab';
import CommTab from './CommTab';
import MeasuresTab from './MeasuresTab';
import ExpediteTab from './ExpediteTab';
import VendorBidsTab from './VendorBidsTab';
import PriceBreaksTab from './PriceBreaksTab';
import LineChangesTab from './LineChangesTab';
import OnHandInventoryTab from './OnHandInventoryTab';
import ExternalSourceTab from './ExternalSourceTab';

const LinesTab = ({ quoteData, setQuoteData }) => {
  const [activeSubTab, setActiveSubTab] = useState('ref');
  const [selectedLineId, setSelectedLineId] = useState(1);
  
  // Sample line items data
  const [lineItems, setLineItems] = useState([
    {
      id: 1,
      line: 1,
      print: true,
      type: '01',
      sts: '0',
      partNumber: '0851MC',
      description: 'PITOT PROBE',
      qtyReq: 1,
      um: 'EA',
      unitPrice: 0.00,
      umPrice: 'EA',
      qtyBid: 0,
      cond: 'SV',
      dlv: 0,
      ring: 'Days',
      total: 0.00
    },
    {
      id: 2,
      line: 2,
      print: true,
      type: '01',
      sts: '0',
      partNumber: '9105A0005-02',
      description: 'TEMP SENSOR',
      qtyReq: 1,
      um: 'EA',
      unitPrice: 0.00,
      umPrice: 'EA',
      qtyBid: 0,
      cond: 'SV',
      dlv: 0,
      ring: 'Days',
      total: 0.00
    }
  ]);

  // Sample line detail data for selected line
  const [lineDetail, setLineDetail] = useState({
    mfg: '',
    cage: '',
    custPN: '',
    testRep: '',
    rev: '',
    shipOn: '',
    noCharge: false,
    dueOn: '',
    priority: '',
    reqPN: '',
    mfgLotNo: '',
    traceTo: 'SAS',
    tagInfo: 'ADG REACTION',
    note: '',
    targetPrice: 0.00,
    serialNumber: '441339',
    shipTo: 0,
    mfgDetail: '',
    category: '34'
  });

  const columns = useMemo(() => [
    {
      accessorKey: 'line',
      header: 'Line',
      size: 60,
      Cell: ({ row }) => (
        <Text 
          size="sm" 
          style={{ 
            cursor: 'pointer',
            color: row.original.id === selectedLineId ? 'blue' : 'inherit',
            fontWeight: row.original.id === selectedLineId ? 'bold' : 'normal'
          }}
          onClick={() => setSelectedLineId(row.original.id)}
        >
          ▶ {row.original.line}
        </Text>
      )
    },
    {
      accessorKey: 'print',
      header: 'Print',
      size: 60,
      Cell: ({ row }) => (
        <Checkbox 
          checked={row.original.print}
          onChange={(event) => {
            const newItems = lineItems.map(item => 
              item.id === row.original.id 
                ? { ...item, print: event.currentTarget.checked }
                : item
            );
            setLineItems(newItems);
          }}
        />
      )
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 60
    },
    {
      accessorKey: 'sts',
      header: 'Sts',
      size: 50
    },
    {
      accessorKey: 'partNumber',
      header: 'P/N',
      size: 120
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 200,
      grow: true
    },
    {
      accessorKey: 'qtyReq',
      header: 'Q. Req',
      size: 80,
      Cell: ({ cell }) => cell.getValue()?.toLocaleString()
    },
    {
      accessorKey: 'um',
      header: 'UM',
      size: 60
    },
    {
      accessorKey: 'unitPrice',
      header: 'Unit Price',
      size: 100,
      Cell: ({ cell }) => cell.getValue()?.toFixed(2)
    },
    {
      accessorKey: 'umPrice',
      header: 'UM',
      size: 60
    },
    {
      accessorKey: 'qtyBid',
      header: 'Q. Bid',
      size: 80,
      Cell: ({ cell }) => cell.getValue()?.toLocaleString()
    },
    {
      accessorKey: 'cond',
      header: 'Cond',
      size: 60
    },
    {
      accessorKey: 'dlv',
      header: 'Dlv',
      size: 60
    },
    {
      accessorKey: 'ring',
      header: 'Ring',
      size: 80
    },
    {
      accessorKey: 'total',
      header: 'Total',
      size: 100,
      Cell: ({ cell }) => cell.getValue()?.toFixed(2)
    }
  ], [lineItems, selectedLineId, setSelectedLineId, setLineItems]);

  const table = useMantineReactTable({
    columns,
    data: lineItems,
    enableRowSelection: false,
    enableColumnOrdering: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    mantineTableBodyRowProps: ({ row }) => ({
      style: {
        backgroundColor: row.original.id === selectedLineId ? '#f0f8ff' : 'transparent'
      }
    }),
    mantineTableProps: {
      style: { fontSize: '12px' }
    }
  });

  return (
    <Box p="md">
      {/* Lines Table */}
      <Box style={{ height: '120px', overflow: 'auto', marginBottom: '16px' }}>
        <MantineReactTable table={table} />
      </Box>

      {/* Line Detail Tabs */}
      <Tabs value={activeSubTab} onChange={setActiveSubTab}>
        <Tabs.List>
          <Tabs.Tab value="ref">Ref</Tabs.Tab>
          <Tabs.Tab value="charges">Charges</Tabs.Tab>
          <Tabs.Tab value="cost">Cost</Tabs.Tab>
          <Tabs.Tab value="comm">Comm</Tabs.Tab>
          <Tabs.Tab value="measures">Measures</Tabs.Tab>
          <Tabs.Tab value="expedite">Expedite</Tabs.Tab>
          <Tabs.Tab value="vendorbids">Vendor Bids</Tabs.Tab>
          <Tabs.Tab value="pricebreaks">Price Breaks</Tabs.Tab>
          <Tabs.Tab value="linechanges">Line Changes</Tabs.Tab>
          <Tabs.Tab value="onhand">On-Hand Inventory</Tabs.Tab>
          <Tabs.Tab value="external">External Source</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="ref">
          <RefTab 
            selectedLineId={selectedLineId}
            lineDetail={lineDetail}
            setLineDetail={setLineDetail}
          />
        </Tabs.Panel>

        <Tabs.Panel value="charges">
          <ChargesTab selectedLineId={selectedLineId} />
        </Tabs.Panel>

        <Tabs.Panel value="cost">
          <CostTab selectedLineId={selectedLineId} />
        </Tabs.Panel>

        <Tabs.Panel value="comm">
          <CommTab selectedLineId={selectedLineId} />
        </Tabs.Panel>

        <Tabs.Panel value="measures">
          <MeasuresTab selectedLineId={selectedLineId} />
        </Tabs.Panel>

        <Tabs.Panel value="expedite">
          <ExpediteTab selectedLineId={selectedLineId} />
        </Tabs.Panel>

        <Tabs.Panel value="vendorbids">
          <VendorBidsTab selectedLineId={selectedLineId} />
        </Tabs.Panel>

        <Tabs.Panel value="pricebreaks">
          <PriceBreaksTab selectedLineId={selectedLineId} />
        </Tabs.Panel>

        <Tabs.Panel value="linechanges">
          <LineChangesTab selectedLineId={selectedLineId} />
        </Tabs.Panel>

        <Tabs.Panel value="onhand">
          <OnHandInventoryTab selectedLineId={selectedLineId} />
        </Tabs.Panel>

        <Tabs.Panel value="external">
          <ExternalSourceTab selectedLineId={selectedLineId} />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
};

export default LinesTab;
